import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { IAppConfigService } from '@nibtravel/shared/util/app-config';
import { APP_STAGE, APP_TENANT } from '@nibtravel/shared/util/app-tenancy';
import { DomService } from '@nibtravel/shared/util/dom';
import {
  ApiUrl,
  IApiUrl,
  IAppConfig,
  Stage,
} from '@nibtravel/shared/util/models';
import { Observable, of } from 'rxjs';

import { environment } from '../environments/environment';

/**
 * This is a custom implementation of the app config for the QQC app.
 * It's a much simpler version of it because getting the branding and environment
 * from http response headers causes a lot of issues regarding loading remote assets
 */
@Injectable()
export class CustomAppConfigService implements IAppConfigService {
  constructor(
    @Inject(APP_TENANT) private brand: string,
    @Inject(APP_STAGE) private stage: string | Stage
  ) {}

  /**
   * This implementation relies on environment.ts being properly set during compilation
   * In addition, the brand is expected to come from the a window variable. This value
   * should be provided by the api lambda handler during runtime.
   *
   * The placeholder for the variable is also generated during compilation time via
   * NX Custom Executor (tools/executors/widget/widget.impl.ts).
   */
  loadBrandConfig(): Observable<IAppConfig> {
    const brandConfig = { ...(environment[this.brand] as IAppConfig) };
    brandConfig.brand = this.brand;
    brandConfig.contentful = environment.contentful;
    brandConfig.sumo = environment.sumo;
    brandConfig.stage = this.stage;
    return of(this.buildFullUrls(brandConfig));
  }

  private buildFullUrls(brandConfig: IAppConfig): IAppConfig {
    const apiUrlInstance = (api: IApiUrl) => new ApiUrl(api.url, api.version);

    if (brandConfig) {
      Object.keys(brandConfig).forEach((key) => {
        if (brandConfig[key] && (brandConfig[key] as IApiUrl).url) {
          brandConfig[key] = apiUrlInstance(brandConfig[key] as IApiUrl);
        }
      });
    }

    return brandConfig;
  }
}
